import { useId, type ReactNode } from "react";
import { classNames } from "./component-model";

export interface CardProps {
  readonly as?: "article" | "section" | "aside" | "div";
  readonly id?: string;
  readonly className?: string;
  readonly tone?: "neutral" | "emphasis" | "inverse";
  readonly headingLevel?: 2 | 3 | 4;
  readonly eyebrow?: ReactNode;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly media?: ReactNode;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
}

export function Card({
  as: Component = "article",
  id,
  className,
  tone = "neutral",
  headingLevel = 2,
  eyebrow,
  title,
  description,
  media,
  actions,
  children,
  footer,
}: CardProps) {
  const generatedId = useId();
  const headingId = `${id ?? `wh-card-${generatedId}`}-title`;
  const Heading = `h${headingLevel}` as const;

  return (
    <Component
      id={id}
      className={classNames("wh-card", `wh-card--${tone}`, className)}
      aria-labelledby={headingId}
    >
      {media && <div className="wh-card__media">{media}</div>}
      <header className="wh-card__header">
        <div>
          {eyebrow && <p className="wh-card__eyebrow">{eyebrow}</p>}
          <Heading className="wh-card__title" id={headingId}>
            {title}
          </Heading>
          {description && <p className="wh-card__description">{description}</p>}
        </div>
        {actions && <div className="wh-card__actions">{actions}</div>}
      </header>
      {children && <div className="wh-card__body">{children}</div>}
      {footer && <footer className="wh-card__footer">{footer}</footer>}
    </Component>
  );
}
